import React from 'react';
import { usePromptButton } from '../PromptButton';
import IconButton from '../../v2BaseComponents/IconButton';
import Button from '../../v2BaseComponents/Button';
import { getIntl } from '../../../utils/intl';
import { LayoutChangeEvent, View } from 'react-native';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import Popover from '../../v2BaseComponents/Popover';
import Stack, { ContentStack, contentStackSpacing } from '../../v2BaseComponents/Stack';
import { mainContentPadding } from '../../v2BaseComponents/MainContentContainer';
import Divider from '../../v2BaseComponents/Divider';
import Text from '../../v2BaseComponents/Text';
import { IconName } from '../../v2BaseComponents/Icon';

const intl = getIntl('FilterButton', {
    reset: 'Reset',
    filters: 'Filters',
});

export type FilterButtonOwnProps = {
    activeFilterCount?: number;
    renderFilterContents: () => React.ReactNode;
    onResetFilters?: () => void;
    filterPromptTitle: string;
    buttonIcon?: IconName;
    buttonTestID?: string;
    popoverTitle?: string;
};

export type FilterButtonProps = FilterButtonOwnProps;

const FilterButton: React.FC<FilterButtonProps> = (props) => {
    const isMobile = useIsMobileLayout();
    const renderButton = (buttonProps: {
        onPress: () => void;
        onLayout?: (e: LayoutChangeEvent) => void;
        ref?: any;
        isDisabled: boolean;
    }) => {
        return (
            <IconButton
                icon={props.buttonIcon || 'Filter'}
                testID={props.buttonTestID || 'filter-prompt-button'}
                onLayout={buttonProps.onLayout}
                {...buttonProps}
            />
        );
    };
    const { onPress, Prompt } = usePromptButton({
        renderPromptContents: props.renderFilterContents,
        promptTitle: props.filterPromptTitle,
        promptHeaderAction: props.onResetFilters ? (
            <Button
                title={intl('reset')}
                onPress={props.onResetFilters}
                variant="text-dark"
                testID="reset-filters-button"
            />
        ) : undefined,
        promptHeaderIcon: 'Filter',
    });

    if (isMobile) {
        return (
            <>
                {renderButton({
                    onPress,
                    isDisabled: false,
                })}
                {Prompt}
            </>
        );
    } else {
        return (
            <Popover
                renderButton={({ onPress, ref, onLayout, isDisabled }) =>
                    renderButton({ onPress, ref, onLayout, isDisabled })
                }
                popoverPosition="top-right-align"
                popoverWidth={314}
            >
                {() => (
                    <ContentStack>
                        <Stack
                            horizontal
                            fullWidth
                            justify="space-between"
                            align="center"
                            style={{
                                paddingTop: mainContentPadding,
                                paddingLeft: mainContentPadding,
                                paddingRight: mainContentPadding,
                            }}
                        >
                            <Text variant="h6">{props.popoverTitle || intl('filters')}</Text>
                            <View>
                                {props.onResetFilters ? (
                                    <Button
                                        title={intl('reset')}
                                        onPress={() => props.onResetFilters?.()}
                                        variant="text-dark"
                                        testID="reset-filters-button"
                                        style={{ marginRight: 8 }}
                                    />
                                ) : null}
                            </View>
                        </Stack>
                        <Divider horizontal />
                        <Stack
                            fullWidth
                            vertical
                            space={contentStackSpacing}
                            style={{
                                paddingBottom: mainContentPadding,
                                paddingLeft: mainContentPadding,
                                paddingRight: mainContentPadding,
                            }}
                        >
                            {props.renderFilterContents()}
                        </Stack>
                    </ContentStack>
                )}
            </Popover>
        );
    }
};

export default React.memo(FilterButton);
